import { Box, Text, theme } from "@aksara-ui/react";
import React, { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ArcElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line, Pie } from "react-chartjs-2";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import {
  getMembersGrowthPerPortal,
  getTotalMembersByProvince,
  getTotalPortalMembers,
} from "redux/actions";
import { InputSelect } from "components/Form/InputSelect";
import ChartDataLabels from "chartjs-plugin-datalabels";

const HeaderWrapper = styled(Box)`
  justify-content: space-between;
  margin-bottom: 30px;
  gap: 20px;
`;

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

interface ValueSchema {
  label: string;
  value: string | number;
}

const MembershipSummaryContent: React.FC = () => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ maxWidth: 576 });

  const [type, setType] = useState<ValueSchema | null>(null);

  const [typeItems] = React.useState([
    { label: "Month", value: "month" },
    { label: "Day", value: "day" },
    { label: "Year", value: "year" },
  ]);

  const {
    tenantPortals,
    totalPortalMembers,
    totalMembersGrowthPerPortal,
    provinces,
    totalMembersByProvince,
    totalTierMemberByProvince,
    totalTierSilverByProvince,
    totalTierGoldByProvince,
    totalTierPlatinumByProvince,
  } = useSelector((state: any) => state.dashboardAnalyticReducer);

  const memberGrowthOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1,
    plugins: {
      legend: {
        position: "bottom" as const,
      },
      title: {
        display: true,
      },
      datalabels: {
        anchor: "end",
        align: "end",
        color: theme.colors.grey07,
        font: {
          weight: "bold",
        },
        formatter: (value: any, context: any) => {
          return parseInt(value).toLocaleString();
        },
      },
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
      },
    },
  };

  const memberGrowthData = {
    labels: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Des",
    ],
    datasets: [
      {
        label: "UltraOddinary",
        data: totalMembersGrowthPerPortal.UltraOddinary,
        // data dummy: [150, 250, 225, 250, 265, 300, 300, 350, 375, 400, 400, 425],
        backgroundColor: "rgba(253, 203, 110, 0.8)",
      },
      {
        label: "IcownicPatch",
        data: totalMembersGrowthPerPortal.IcownicPatch,
        // data dummy: [250, 300, 325, 350, 365, 400, 400, 450, 475, 500, 500, 525],
        backgroundColor: "rgba(253, 121, 168, 0.8)",
      },
      {
        label: "Chatbot",
        data: totalMembersGrowthPerPortal.Chatbot,
        // data dummy: [250, 300, 325, 350, 365, 400, 400, 450, 475, 500, 500, 525],
        backgroundColor: "rgba(9, 132, 227, 0.5)",
      },
    ],
  };

  const portalMemberOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1,
    plugins: {
      legend: {
        position: "right" as const,
      },
      title: {
        display: true,
      },
      datalabels: {
        color: theme.colors.gray10,
        font: {
          weight: "bold",
          size: 14,
        },
        formatter: (value: any, context: any) => {
          const dataset = context.dataset.data;
          const total = dataset.reduce(
            (acc: number, curr: number) => acc + curr,
            0
          );
          const percentage = ((value / total) * 100).toFixed(2) + "%";
          return percentage;
        },
      },
    },
  };

  const portalMemberData = {
    labels: tenantPortals,
    datasets: [
      {
        label: "Portal Members",
        data: totalPortalMembers,
        backgroundColor: [
          "rgba(9, 132, 227, 0.5)",
          "rgba(253, 121, 168, 0.8)",
          "rgba(253, 203, 110, 0.8)",
        ],
      },
    ],
  };

  const locationOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1,
    plugins: {
      legend: {
        position: "bottom" as const,
      },
      title: {
        display: true,
      },
      datalabels: {
        anchor: "end",
        align: "end",
        color: theme.colors.grey07,
        font: {
          weight: "bold",
        },
        formatter: (value: any, context: any) => {
          return parseInt(value).toLocaleString();
        },
      },
    },
  };

  const locationData = {
    labels: provinces,
    datasets: [
      {
        label: "Location",
        data: totalMembersByProvince,
        // data dummy: [630, 400, 450, 550, 550, 590],
        backgroundColor: ["rgba(99, 205, 218, 0.8)"],
      },
    ],
  };

  const membershipLevelLocationOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1,
    plugins: {
      legend: {
        position: "bottom" as const,
      },
      title: {
        display: true,
      },
      datalabels: {
        // anchor: "end",
        align: "end",
        color: theme.colors.grey07,
        font: {
          weight: "bold",
        },
        formatter: (value: any, context: any) => {
          return parseInt(value).toLocaleString();
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: true,
        },
      },
      y: {
        grid: {
          display: true,
        },
      },
    },
  };

  const membershipLevelLocationData = {
    labels: provinces,
    datasets: [
      {
        label: "Location",
        data: totalMembersByProvince,
        // data dummy: [630, 400, 450, 550, 550, 590],
        backgroundColor: ["rgba(96, 163, 188, 0.6)"],
        type: "bar" as any,
        order: 1,
      },
      {
        label: "Member",
        data: totalTierMemberByProvince,
        // data: [220, 300, 350],
        backgroundColor: "rgba(243, 169, 96, 1)",
        borderColor: "rgba(243, 169, 96, 1)",
        fill: false,
        pointHoverRadius: 10,
        pointHoverBorderWidth: 3,
        order: 0,
      },
      {
        label: "Silver",
        data: totalTierSilverByProvince,
        // data: [220, 300, 350],
        backgroundColor: "rgba(223, 228, 234, 1)",
        borderColor: "rgba(223, 228, 234, 1)",
        fill: false,
        pointHoverRadius: 10,
        pointHoverBorderWidth: 3,
        order: 0,
      },
      {
        label: "Gold",
        data: totalTierGoldByProvince,
        // data: [300, 100, 200],
        backgroundColor: "rgba(236, 204, 104, 1)",
        borderColor: "rgba(236, 204, 104, 1)",
        fill: false,
        pointHoverRadius: 10,
        pointHoverBorderWidth: 3,
        order: 0,
      },
      {
        label: "Platinum",
        data: totalTierPlatinumByProvince,
        // data: [480, 200, 280],
        backgroundColor: "rgba(164, 176, 190, 0.8)",
        borderColor: "rgba(164, 176, 190, 0.8)",
        fill: false,
        pointHoverRadius: 10,
        pointHoverBorderWidth: 3,
        order: 0,
      },
    ],
  };

  useEffect(() => {
    dispatch(getTotalPortalMembers() as any);
    dispatch(getMembersGrowthPerPortal() as any);
    dispatch(getTotalMembersByProvince() as any);
  }, [dispatch]);

  return (
    <Box height={"100%"} paddingY={20} paddingX={isMobile ? 30 : 50}>
      <HeaderWrapper display={"flex"} flexDirection={"column"}>
        <Box display={"flex"}>
          <Text scale={[500, 600]} fontWeight={700}>
            Membership Summary
          </Text>
        </Box>

        <Box display={"flex"} alignItems={"center"}>
          <Text color="grey07" fontWeight={700} marginRight={10}>
            View By
          </Text>
          <Box width={"136px"}>
            <InputSelect
              selectedItem={type}
              placeholder="Select Type"
              handleSelectedItemChange={({ selectedItem }) => {
                if (selectedItem) {
                  setType(selectedItem);
                }
              }}
              itemToString={(item) => (item ? `${item.label}` : "")}
              itemValue={(item) => (item ? `${item.value}` : "")}
              items={typeItems}
              size={"lg"}
              borderRadius={"6px"}
            />
          </Box>
        </Box>
      </HeaderWrapper>

      <Box
        display={"flex"}
        justifyContent={"space-between"}
        flexDirection={isMobile ? "column" : "row"}
        marginBottom={50}
        style={{ gap: 50 }}
      >
        <Box width={isMobile ? "100%" : "60%"} height={400}>
          <Text color="grey07" scale={400} fontWeight={"bold"}>
            MEMBER GROWTH BY PORTAL
          </Text>

          <Bar options={memberGrowthOptions as any} data={memberGrowthData} />
        </Box>

        <Box width={isMobile ? "100%" : "35%"} height={350}>
          <Text color="grey07" scale={400} fontWeight={"bold"}>
            MEMBERSHIP BY PORTAL
          </Text>

          <Pie options={portalMemberOptions as any} data={portalMemberData} />
        </Box>
      </Box>

      <Box
        display={"flex"}
        justifyContent={"space-between"}
        flexDirection={isMobile ? "column" : "row"}
        marginBottom={50}
        style={{ gap: 50 }}
      >
        <Box width={isMobile ? "100%" : "49%"} height={400}>
          <Text color="grey07" scale={400} fontWeight={"bold"}>
            LOCATION
          </Text>

          <Bar options={locationOptions as any} data={locationData} />
        </Box>

        <Box width={isMobile ? "100%" : "49%"} height={400}>
          <Text color="grey07" scale={400} fontWeight={"bold"}>
            MEMBERSHIP LEVEL BY LOCATION
          </Text>

          <Line
            options={membershipLevelLocationOptions as any}
            data={membershipLevelLocationData}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default MembershipSummaryContent;
